"use client"

// ============================================================
// GRAVITATIONAL NEBULA — WebGPU hero background
// ------------------------------------------------------------
// Ported onto the shared GPU platform (modules/core/gpu). The component only
// declares setup / frame / resize / teardown; the platform owns the device,
// surface health, frame loop, resize, fallback, and device-lost recovery.
//
// Multi-pass pipeline (unchanged):
//   1. Compute — particle physics       3. Bloom — bright extract + gaussian
//   2. Render particles → HDR texture   4. Composite — scene + bloom → screen
// ============================================================

import * as React from "react"

import {
  useGpuEffect,
  type WebGPUSetupContext,
  type WebGPUFrameContext,
} from "@/core/gpu/use-gpu-effect"

const COMPUTE_SHADER = /* wgsl */ `
struct Particle {
  pos: vec3<f32>,
  life: f32,
  vel: vec3<f32>,
  speed: f32,
};
struct Uniforms {
  mouse: vec2<f32>,
  resolution: vec2<f32>,
  time: f32,
  dt: f32,
  _p0: f32,
  _p1: f32,
};
@group(0) @binding(0) var<uniform> u: Uniforms;
@group(0) @binding(1) var<storage, read> src: array<Particle>;
@group(0) @binding(2) var<storage, read_write> dst: array<Particle>;

fn hash(n: f32) -> f32 { return fract(sin(n) * 43758.5453123); }
fn noise3D(x: vec3<f32>) -> f32 {
  let p = floor(x);
  let f = fract(x);
  let w = f * f * (3.0 - 2.0 * f);
  let n = p.x + p.y * 157.0 + 113.0 * p.z;
  return mix(
    mix(mix(hash(n), hash(n+1.0), w.x), mix(hash(n+157.0), hash(n+158.0), w.x), w.y),
    mix(mix(hash(n+113.0), hash(n+114.0), w.x), mix(hash(n+270.0), hash(n+271.0), w.x), w.y),
    w.z
  );
}
fn curlNoise(p: vec3<f32>) -> vec3<f32> {
  let e = 0.05;
  let dx = vec3<f32>(e, 0.0, 0.0);
  let dy = vec3<f32>(0.0, e, 0.0);
  let dz = vec3<f32>(0.0, 0.0, e);
  return vec3<f32>(
    noise3D(p + dy) - noise3D(p - dy) - noise3D(p + dz) + noise3D(p - dz),
    noise3D(p + dz) - noise3D(p - dz) - noise3D(p + dx) + noise3D(p - dx),
    noise3D(p + dx) - noise3D(p - dx) - noise3D(p + dy) + noise3D(p - dy)
  ) / (2.0 * e);
}
fn pcg(v_in: u32) -> u32 {
  var v = v_in * 747796405u + 2891336453u;
  let w = ((v >> ((v >> 28u) + 4u)) ^ v) * 277803737u;
  return (w >> 22u) ^ w;
}
fn randu(s: u32) -> f32 { return f32(pcg(s)) / 4294967295.0; }

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= arrayLength(&src)) { return; }
  var p = src[i];
  let aspect = u.resolution.x / u.resolution.y;
  let flowPos = p.pos * 0.8 + vec3<f32>(u.time * 0.05, 0.0, u.time * 0.08);
  p.vel += curlNoise(flowPos) * 0.3 * u.dt;
  let mw = vec3<f32>((u.mouse.x * 2.0 - 1.0) * aspect, -(u.mouse.y * 2.0 - 1.0), 0.0);
  let toM = mw - p.pos;
  let dist = max(length(toM), 0.01);
  if (dist < 3.5) {
    p.vel += normalize(toM) * 0.12 * smoothstep(3.5, 0.0, dist) * u.dt;
  }
  p.vel *= 0.96;
  p.pos += p.vel * u.dt;
  p.speed = length(p.vel);
  p.life -= u.dt * (0.03 + hash(f32(i) * 0.17) * 0.05);
  if (p.life <= 0.0 || abs(p.pos.x) > aspect * 2.0 || abs(p.pos.y) > 2.0) {
    let s1 = pcg(i + u32(u.time * 1000.0));
    let s2 = pcg(s1); let s3 = pcg(s2);
    p.pos = vec3<f32>(
      (randu(s1) * 2.0 - 1.0) * aspect * 1.5,
      (randu(s2) * 2.0 - 1.0) * 1.5,
      (randu(s3) * 2.0 - 1.0) * 0.8
    );
    p.vel = vec3<f32>(0.0);
    p.life = 1.0 + randu(pcg(s3)) * 3.0;
    p.speed = 0.0;
  }
  dst[i] = p;
}
`

const RENDER_SHADER = /* wgsl */ `
struct Particle { pos: vec3<f32>, life: f32, vel: vec3<f32>, speed: f32, };
struct Uniforms { mouse: vec2<f32>, resolution: vec2<f32>, time: f32, dt: f32, _p0: f32, _p1: f32, };
@group(0) @binding(0) var<uniform> u: Uniforms;
@group(0) @binding(1) var<storage, read> particles: array<Particle>;
struct VSOut { @builtin(position) pos: vec4<f32>, @location(0) color: vec4<f32>, @location(1) uv: vec2<f32>, };
@vertex
fn vs_main(@builtin(vertex_index) vid: u32, @builtin(instance_index) iid: u32) -> VSOut {
  let p = particles[iid];
  let aspect = u.resolution.x / u.resolution.y;
  var q = array<vec2<f32>, 6>(
    vec2(-1.0, -1.0), vec2(1.0, -1.0), vec2(-1.0, 1.0),
    vec2(-1.0,  1.0), vec2(1.0, -1.0), vec2( 1.0, 1.0)
  );
  let corner = q[vid];
  let depthScale = 0.7 + (p.pos.z + 0.8) * 0.4;
  let size = (0.003 + p.speed * 0.008) * max(depthScale, 0.3);
  let ndc = vec2<f32>(p.pos.x / aspect, p.pos.y);
  var out: VSOut;
  out.pos = vec4<f32>(ndc + corner * size * vec2(1.0 / aspect, 1.0), 0.0, 1.0);
  out.uv = corner;
  let fade = smoothstep(0.0, 0.4, p.life) * smoothstep(0.0, 1.0, p.life);
  let t = clamp(p.speed * 25.0, 0.0, 1.0);
  let c1 = vec3<f32>(0.2, 0.05, 0.45);
  let c2 = vec3<f32>(0.1, 0.3, 0.9);
  let c3 = vec3<f32>(0.0, 0.7, 1.0);
  let c4 = vec3<f32>(0.6, 0.95, 1.0);
  var color = mix(c1, c2, smoothstep(0.0, 0.25, t));
  color = mix(color, c3, smoothstep(0.25, 0.6, t));
  color = mix(color, c4, smoothstep(0.6, 1.0, t));
  let emissive = 0.8 + t * 0.6;
  color *= emissive;
  out.color = vec4<f32>(color, fade * 0.22);
  return out;
}
@fragment
fn fs_main(in: VSOut) -> @location(0) vec4<f32> {
  let d = length(in.uv);
  let glow = exp(-d * d * 3.5);
  let a = glow * in.color.a;
  if (a < 0.001) { discard; }
  return vec4<f32>(in.color.rgb * glow, a);
}
`

const BLUR_SHADER = /* wgsl */ `
@group(0) @binding(0) var srcTex: texture_2d<f32>;
@group(0) @binding(1) var srcSampler: sampler;
@group(0) @binding(2) var<uniform> direction: vec4<f32>;
struct VSOut { @builtin(position) pos: vec4<f32>, @location(0) uv: vec2<f32>, };
@vertex
fn vs_fullscreen(@builtin(vertex_index) vid: u32) -> VSOut {
  var positions = array<vec2<f32>, 3>(vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  var uvs = array<vec2<f32>, 3>(vec2(0.0, 1.0), vec2(2.0, 1.0), vec2(0.0, -1.0));
  var out: VSOut; out.pos = vec4<f32>(positions[vid], 0.0, 1.0); out.uv = uvs[vid]; return out;
}
@fragment
fn fs_blur(in: VSOut) -> @location(0) vec4<f32> {
  let w = array<f32, 5>(0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216);
  let step = direction.xy * direction.zw * 2.0;
  var result = textureSample(srcTex, srcSampler, in.uv) * w[0];
  for (var i = 1; i < 5; i++) {
    let offset = step * f32(i);
    result += textureSample(srcTex, srcSampler, in.uv + offset) * w[i];
    result += textureSample(srcTex, srcSampler, in.uv - offset) * w[i];
  }
  return result;
}
`

const COMPOSITE_SHADER = /* wgsl */ `
@group(0) @binding(0) var srcTex: texture_2d<f32>;
@group(0) @binding(1) var srcSampler: sampler;
struct VSOut { @builtin(position) pos: vec4<f32>, @location(0) uv: vec2<f32>, };
@vertex
fn vs_fullscreen(@builtin(vertex_index) vid: u32) -> VSOut {
  var positions = array<vec2<f32>, 3>(vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  var uvs = array<vec2<f32>, 3>(vec2(0.0, 1.0), vec2(2.0, 1.0), vec2(0.0, -1.0));
  var out: VSOut; out.pos = vec4<f32>(positions[vid], 0.0, 1.0); out.uv = uvs[vid]; return out;
}
@fragment
fn fs_composite(in: VSOut) -> @location(0) vec4<f32> {
  let color = textureSample(srcTex, srcSampler, in.uv);
  return vec4<f32>(color.rgb * 1.2, 1.0);
}
`

/**
 * All GPU resources for the nebula. Size-independent things (buffers, pipelines,
 * sampler, compute/render bind groups) are built once in the constructor; the
 * size-dependent HDR/bloom textures and their bind groups live in resizeTargets
 * so the platform's resize path can rebuild only those.
 */
class Nebula {
  private device: GPUDevice
  private W: number
  private H: number

  private bufA: GPUBuffer
  private bufB: GPUBuffer
  private uBuf: GPUBuffer
  private uData = new Float32Array(8)
  private blurHBuf: GPUBuffer
  private blurVBuf: GPUBuffer
  private sampler: GPUSampler

  private compPipe: GPUComputePipeline
  private rendPipe: GPURenderPipeline
  private blurPipe: GPURenderPipeline
  private sceneScreenPipe: GPURenderPipeline
  private bloomCompPipe: GPURenderPipeline

  private compBG0: GPUBindGroup
  private compBG1: GPUBindGroup
  private rendBG0: GPUBindGroup
  private rendBG1: GPUBindGroup

  private sceneTex!: GPUTexture
  private bloomTexA!: GPUTexture
  private bloomTexB!: GPUTexture
  private blurBG_H!: GPUBindGroup
  private blurBG_V!: GPUBindGroup
  private sceneBG!: GPUBindGroup
  private bloomBG!: GPUBindGroup

  private frame = 0
  private mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 }
  private readonly particleCount: number

  private onMouse = (e: MouseEvent) => {
    this.mouse.tx = e.clientX / window.innerWidth
    this.mouse.ty = e.clientY / window.innerHeight
  }
  private onTouch = (e: TouchEvent) => {
    if (e.touches.length > 0) {
      this.mouse.tx = e.touches[0].clientX / window.innerWidth
      this.mouse.ty = e.touches[0].clientY / window.innerHeight
    }
  }

  constructor(ctx: WebGPUSetupContext, particleCount: number) {
    this.device = ctx.device
    this.particleCount = particleCount
    this.W = ctx.width
    this.H = ctx.height
    const { device, format } = ctx

    const compMod = device.createShaderModule({ code: COMPUTE_SHADER })
    const rendMod = device.createShaderModule({ code: RENDER_SHADER })
    const blurMod = device.createShaderModule({ code: BLUR_SHADER })
    const compsMod = device.createShaderModule({ code: COMPOSITE_SHADER })

    // Particle buffers (double-buffered).
    const data = new Float32Array(particleCount * 8)
    const aspect = this.W / this.H
    for (let i = 0; i < particleCount; i++) {
      const o = i * 8
      data[o] = (Math.random() * 2 - 1) * aspect * 1.5
      data[o + 1] = (Math.random() * 2 - 1) * 1.5
      data[o + 2] = (Math.random() * 2 - 1) * 0.8
      data[o + 3] = Math.random() * 3.0 + 0.5
    }
    this.bufA = device.createBuffer({
      size: data.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true,
    })
    new Float32Array(this.bufA.getMappedRange()).set(data)
    this.bufA.unmap()
    this.bufB = device.createBuffer({
      size: data.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    })

    this.uBuf = device.createBuffer({ size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST })
    this.sampler = device.createSampler({ magFilter: "linear", minFilter: "linear" })
    this.blurHBuf = device.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST })
    this.blurVBuf = device.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST })

    // Pipelines.
    this.compPipe = device.createComputePipeline({
      layout: "auto",
      compute: { module: compMod, entryPoint: "main" },
    })
    this.rendPipe = device.createRenderPipeline({
      layout: "auto",
      vertex: { module: rendMod, entryPoint: "vs_main" },
      fragment: {
        module: rendMod,
        entryPoint: "fs_main",
        targets: [
          {
            format: "rgba16float",
            blend: {
              color: { srcFactor: "src-alpha", dstFactor: "one", operation: "add" },
              alpha: { srcFactor: "zero", dstFactor: "one", operation: "add" },
            },
          },
        ],
      },
      primitive: { topology: "triangle-list" },
    })
    this.blurPipe = device.createRenderPipeline({
      layout: "auto",
      vertex: { module: blurMod, entryPoint: "vs_fullscreen" },
      fragment: { module: blurMod, entryPoint: "fs_blur", targets: [{ format: "rgba16float" }] },
      primitive: { topology: "triangle-list" },
    })
    this.sceneScreenPipe = device.createRenderPipeline({
      layout: "auto",
      vertex: { module: compsMod, entryPoint: "vs_fullscreen" },
      fragment: { module: compsMod, entryPoint: "fs_composite", targets: [{ format }] },
      primitive: { topology: "triangle-list" },
    })
    this.bloomCompPipe = device.createRenderPipeline({
      layout: "auto",
      vertex: { module: compsMod, entryPoint: "vs_fullscreen" },
      fragment: {
        module: compsMod,
        entryPoint: "fs_composite",
        targets: [
          {
            format,
            blend: {
              color: { srcFactor: "one", dstFactor: "one", operation: "add" },
              alpha: { srcFactor: "one", dstFactor: "one", operation: "add" },
            },
          },
        ],
      },
      primitive: { topology: "triangle-list" },
    })

    // Size-independent bind groups.
    this.compBG0 = device.createBindGroup({
      layout: this.compPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uBuf } },
        { binding: 1, resource: { buffer: this.bufA } },
        { binding: 2, resource: { buffer: this.bufB } },
      ],
    })
    this.compBG1 = device.createBindGroup({
      layout: this.compPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uBuf } },
        { binding: 1, resource: { buffer: this.bufB } },
        { binding: 2, resource: { buffer: this.bufA } },
      ],
    })
    this.rendBG0 = device.createBindGroup({
      layout: this.rendPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uBuf } },
        { binding: 1, resource: { buffer: this.bufB } },
      ],
    })
    this.rendBG1 = device.createBindGroup({
      layout: this.rendPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uBuf } },
        { binding: 1, resource: { buffer: this.bufA } },
      ],
    })

    this.buildTargets()

    window.addEventListener("mousemove", this.onMouse)
    window.addEventListener("touchmove", this.onTouch, { passive: true })
  }

  /** (Re)builds the size-dependent HDR + bloom textures and their bind groups. */
  private buildTargets() {
    const { device } = this
    this.sceneTex?.destroy()
    this.bloomTexA?.destroy()
    this.bloomTexB?.destroy()

    this.sceneTex = device.createTexture({
      size: [this.W, this.H],
      format: "rgba16float",
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    })
    const bW = Math.max(1, this.W >> 1)
    const bH = Math.max(1, this.H >> 1)
    this.bloomTexA = device.createTexture({
      size: [bW, bH],
      format: "rgba16float",
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    })
    this.bloomTexB = device.createTexture({
      size: [bW, bH],
      format: "rgba16float",
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    })

    device.queue.writeBuffer(this.blurHBuf, 0, new Float32Array([1, 0, 1 / bW, 1 / bH]))
    device.queue.writeBuffer(this.blurVBuf, 0, new Float32Array([0, 1, 1 / bW, 1 / bH]))

    this.blurBG_H = device.createBindGroup({
      layout: this.blurPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.sceneTex.createView() },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: { buffer: this.blurHBuf } },
      ],
    })
    this.blurBG_V = device.createBindGroup({
      layout: this.blurPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.bloomTexA.createView() },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: { buffer: this.blurVBuf } },
      ],
    })
    this.sceneBG = device.createBindGroup({
      layout: this.sceneScreenPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.sceneTex.createView() },
        { binding: 1, resource: this.sampler },
      ],
    })
    this.bloomBG = device.createBindGroup({
      layout: this.bloomCompPipe.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: this.bloomTexB.createView() },
        { binding: 1, resource: this.sampler },
      ],
    })
  }

  resizeTargets(w: number, h: number) {
    this.W = Math.max(1, w)
    this.H = Math.max(1, h)
    this.buildTargets()
  }

  render(currentTexture: GPUTexture, dt: number, now: number) {
    const { device } = this
    const m = this.mouse
    m.x += (m.tx - m.x) * 0.06
    m.y += (m.ty - m.y) * 0.06

    this.uData[0] = m.x
    this.uData[1] = m.y
    this.uData[2] = this.W
    this.uData[3] = this.H
    this.uData[4] = now / 1000
    this.uData[5] = dt
    device.queue.writeBuffer(this.uBuf, 0, this.uData)

    const enc = device.createCommandEncoder()

    // 1: compute
    const cp = enc.beginComputePass()
    cp.setPipeline(this.compPipe)
    cp.setBindGroup(0, this.frame % 2 === 0 ? this.compBG0 : this.compBG1)
    cp.dispatchWorkgroups(Math.ceil(this.particleCount / 256))
    cp.end()

    // 2: particles → HDR scene
    const rp = enc.beginRenderPass({
      colorAttachments: [
        {
          view: this.sceneTex.createView(),
          clearValue: { r: 0.01, g: 0.01, b: 0.025, a: 1.0 },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    })
    rp.setPipeline(this.rendPipe)
    rp.setBindGroup(0, this.frame % 2 === 0 ? this.rendBG0 : this.rendBG1)
    rp.draw(6, this.particleCount)
    rp.end()

    // 3a: horizontal blur → bloomTexA
    const hb = enc.beginRenderPass({
      colorAttachments: [
        { view: this.bloomTexA.createView(), loadOp: "clear", clearValue: { r: 0, g: 0, b: 0, a: 0 }, storeOp: "store" },
      ],
    })
    hb.setPipeline(this.blurPipe)
    hb.setBindGroup(0, this.blurBG_H)
    hb.draw(3)
    hb.end()

    // 3b: vertical blur → bloomTexB
    const vb = enc.beginRenderPass({
      colorAttachments: [
        { view: this.bloomTexB.createView(), loadOp: "clear", clearValue: { r: 0, g: 0, b: 0, a: 0 }, storeOp: "store" },
      ],
    })
    vb.setPipeline(this.blurPipe)
    vb.setBindGroup(0, this.blurBG_V)
    vb.draw(3)
    vb.end()

    const screenView = currentTexture.createView()

    // 4a: scene → screen
    const sp = enc.beginRenderPass({
      colorAttachments: [
        { view: screenView, clearValue: { r: 0.01, g: 0.01, b: 0.025, a: 1.0 }, loadOp: "clear", storeOp: "store" },
      ],
    })
    sp.setPipeline(this.sceneScreenPipe)
    sp.setBindGroup(0, this.sceneBG)
    sp.draw(3)
    sp.end()

    // 4b: additive bloom → screen
    const bp = enc.beginRenderPass({
      colorAttachments: [{ view: screenView, loadOp: "load", storeOp: "store" }],
    })
    bp.setPipeline(this.bloomCompPipe)
    bp.setBindGroup(0, this.bloomBG)
    bp.draw(3)
    bp.end()

    device.queue.submit([enc.finish()])
    this.frame++
  }

  dispose() {
    window.removeEventListener("mousemove", this.onMouse)
    window.removeEventListener("touchmove", this.onTouch)
    this.sceneTex?.destroy()
    this.bloomTexA?.destroy()
    this.bloomTexB?.destroy()
    this.bufA?.destroy()
    this.bufB?.destroy()
    this.uBuf?.destroy()
    this.blurHBuf?.destroy()
    this.blurVBuf?.destroy()
  }
}

/** Static fallback: reduced-motion, no-WebGPU, or a hard failure. */
function NebulaPoster() {
  return (
    <div
      className="absolute inset-0 w-full h-full"
      style={{
        background:
          "radial-gradient(ellipse 70% 55% at 50% 45%, rgba(30,80,230,0.28), rgba(120,40,220,0.14) 40%, rgba(2,3,8,1) 78%)",
      }}
    />
  )
}

interface Props {
  particleCount?: number
}

export default function GravitationalNebula({ particleCount = 80000 }: Props) {
  return useGpuEffect<Nebula>({
    backend: "webgpu",
    mode: "pauseWork",
    id: "gravitational-nebula",
    priority: "critical",
    className: "absolute inset-0 w-full h-full overflow-hidden z-0",
    alphaMode: "premultiplied",
    fallback: <NebulaPoster />,
    overlay: (
      <>
        <div
          className="absolute inset-0 pointer-events-none z-[1]"
          style={{ background: "radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)" }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none z-[1]"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}
        />
      </>
    ),
    setup: (ctx) => new Nebula(ctx as WebGPUSetupContext, particleCount),
    frame: (nebula, ctx, dt) => {
      const c = ctx as WebGPUFrameContext
      nebula.render(c.currentTexture, dt, c.now)
    },
    resize: (nebula, w, h) => nebula.resizeTargets(w, h),
    teardown: (nebula) => nebula.dispose(),
  })
}
